import { Tooltip } from "~/components/Tooltip";
import { cn } from "~/lib/utils";
import { formatApy } from "~/utils/functions/formatApy";

// // // //

interface Props {
    pct: number | null;
    className?: string;
}

/**
 * Compact label for bridged-token-value (AUM) change over the trailing 30 days.
 * Shows a hover/focus tooltip with context.
 */
export function Aum30dGrowthIndicator(props: Props) {
    const { pct, className } = props;

    if (pct === null || Number.isNaN(pct)) {
        return null;
    }

    const isZero = pct === 0;
    const isPositive = pct > 0;
    const magnitude = Math.round(Math.abs(formatApy(pct))).toLocaleString();
    const signedPct = isZero
        ? "0%"
        : isPositive
          ? `+${magnitude}%`
          : `−${magnitude}%`;
    const displayValue = `(${signedPct})`;

    const textClassName = cn(
        "relative -top-0.5 inline shrink-0 tabular-nums text-[11px] font-normal leading-none cursor-default",
        !isZero && isPositive && "text-emerald-600/70",
        !isZero && !isPositive && "text-rose-600/70",
        isZero && "text-muted-foreground",
        className,
    );

    const ariaLabel = `30D AUM % change ${signedPct}`;

    return (
        <Tooltip
            placement="top"
            tooltipWrapperClassName="border border-muted bg-card px-2.5 py-2 text-xs text-secondary-foreground shadow-lg rounded-lg"
            tooltipContent="30D AUM % change"
        >
            {({ setTriggerRef }) => (
                <span
                    ref={setTriggerRef}
                    className={textClassName}
                    tabIndex={0}
                    aria-label={ariaLabel}
                >
                    {displayValue}
                </span>
            )}
        </Tooltip>
    );
}
